import { useState, useEffect, useRef } from "react";
import { Headset, AlertTriangle, Lightbulb, Plus, GraduationCap } from "lucide-react";
import FeedbackDialog from "./TktDialog";
import { isTrial } from "../../Utils/Utilities";
import QuickTraining from "../../Components/QuickTraining/QuickTraining";
import { getTrainingData } from "../../Components/QuickTraining/QuickTrainingData";
import { motion, AnimatePresence } from "framer-motion";
import useIsMobile from "../../Hooks/useIsMobile";
import { useSubscriptionModalState } from "../../Hooks/useSubscriptionModalState";
import { triggerHaptic } from "../../Hooks/useHaptics";

// Old code: const FloatingFeedbackButton = () => {
const FloatingFeedbackButton = ({ onAddClick, className = "" }) => {
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const [isQuickTrainingOpen, setIsQuickTrainingOpen] = useState(false);
    const [selectedFeedbackType, setSelectedFeedbackType] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const hasCheckedLogin = useRef(false);
    const isMobile = useIsMobile();
    const { isUpgradeModalOpen, isStartSubscriptionModalOpen } = useSubscriptionModalState();
    const isAnySubscriptionModalOpen = isUpgradeModalOpen || isStartSubscriptionModalOpen;

    useEffect(() => {
        if (hasCheckedLogin.current) return;
        hasCheckedLogin.current = true;
        try {
            const getIDB = localStorage.getItem('getIDB');
            if (getIDB === '1') {
                !isMobile && setIsExpanded(false);
                localStorage.removeItem('getIDB'); // Remove the item so it doesn't show again
            } else {
                setIsExpanded(false);
            }
        } catch (error) {
            console.error('Error accessing localStorage:', error);
        }
    }, []);

    const handleFeedbackButtonClick = () => {
        triggerHaptic("soft");
        if (isMobile && onAddClick) {
            // Old code: setIsExpanded(!isExpanded);
            onAddClick();
            return;
        }
        setIsExpanded(!isExpanded);
    };

    const handleOptionClick = (type) => {
        setSelectedFeedbackType(type);
        setIsFeedbackOpen(true);
        setIsExpanded(false);
    };

    const handleFeedbackClose = () => {
        setIsFeedbackOpen(false);
        setSelectedFeedbackType(null);
    };

    return (
        <>
            {/* Dark Background Overlay - covers entire page including header, sidebar, and bottom nav */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        className="fixed inset-0 bg-black z-[50] backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.5 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={handleFeedbackButtonClick}
                    />
                )}
            </AnimatePresence>

            {/* Floating Feedback Button with Dropdown */}
            <motion.div
                className={`fixed bottom-20 right-6 lg:bottom-8 lg:right-8 transition-all duration-300 pointer-events-auto ${className} ${isAnySubscriptionModalOpen ? "z-[10001]" : "z-[50]"
                    }`}
                initial={isMobile ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={isMobile ? { duration: 0 } : {
                    type: "spring",
                    stiffness: 260,
                    damping: 20,
                    delay: 0.5 // Small delay to let the page load first
                }}
            >
                <motion.div
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                    <div className="relative">
                        <button
                            onClick={handleFeedbackButtonClick}
                            className={`w-12 h-12 lg:w-14 lg:h-14 border !border-sahi-primary-color rounded-full shadow-lg flex items-center justify-center group transition-all duration-300 p-0
                          ${isExpanded
                                    ? "bg-white text-sahi-primary-color  shadow-xl"
                                    : "bg-sahi-primary-color text-sahi-white hover:bg-white hover:text-sahi-primary-color hover:shadow-xl"
                                }`}
                        >
                            <motion.div
                                animate={{ rotate: isExpanded ? 45 : 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                {isExpanded ? (
                                    <Plus className="h-5 w-5 lg:h-6 lg:w-6" />
                                ) : isMobile ? (
                                    // Render Plus icon in mobile view instead of Headset icon
                                    // Old code: <Headset className="h-5 w-5 lg:h-6 lg:w-6" />
                                    <Plus className="h-5 w-5 lg:h-6 lg:w-6" />
                                ) : (
                                    <Headset className="h-5 w-5 lg:h-6 lg:w-6" />
                                )}
                            </motion.div>
                        </button>


                        {/* Subtle pulse animation - disabled on mobile view */}
                        {/* Old code:
                        {!isExpanded && (
                            <span className="absolute inset-0 rounded-full bg-sahi-primary-color animate-ping opacity-20 pointer-events-none"></span>
                        )}
                        */}
                        {!isExpanded && !isMobile && (
                            <span className="absolute inset-0 rounded-full bg-sahi-primary-color animate-ping opacity-20 pointer-events-none"></span>
                        )}
                    </div>

                    {/* Expandable Options */}
                    <AnimatePresence>
                        {isExpanded && (
                            <motion.div
                                className="absolute bottom-16 lg:bottom-20 right-0 flex flex-col gap-3"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 20 }}
                                transition={{ duration: 0.3, staggerChildren: 0.1 }}
                            >
                                {/* Training Button */}
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    transition={{ delay: 0.05 }}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="flex items-center gap-3 justify-end"
                                >
                                    <button
                                        onClick={() => {
                                            setIsQuickTrainingOpen(true);
                                            setIsExpanded(false);
                                        }}
                                        className="bg-white text-sahi-dark-blue px-3 py-2 rounded-lg shadow-md whitespace-nowrap text-sm font-medium transition-all duration-300 hover:shadow-lg cursor-pointer"
                                    >
                                        Training
                                    </button>
                                    <button
                                        onClick={() => {
                                            setIsQuickTrainingOpen(true);
                                            setIsExpanded(false);
                                        }}
                                        className="w-10 h-10 lg:w-12 lg:h-12 rounded-full shadow-lg bg-sahi-dark-blue text-white flex items-center justify-center transition-all duration-300 hover:shadow-xl flex-shrink-0"
                                        title="Quick Training"
                                    >
                                        <GraduationCap className="h-4 w-4 lg:h-5 lg:w-5" />
                                    </button>
                                </motion.div>

                                {/* Help Request Button */}
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    transition={{ delay: 0.1 }}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="flex items-center gap-3 justify-end hidden"
                                >
                                    <button
                                        onClick={() => handleOptionClick(3)}
                                        className="bg-white text-sahi-orange px-3 py-2 rounded-lg shadow-md whitespace-nowrap text-sm font-medium transition-all duration-300 hover:shadow-lg cursor-pointer"
                                    >
                                        Help Request
                                    </button>
                                    <button
                                        onClick={() => handleOptionClick(3)}
                                        className="w-10 h-10 lg:w-12 lg:h-12 rounded-full shadow-lg bg-sahi-orange text-white flex items-center justify-center transition-all duration-300 hover:shadow-xl flex-shrink-0"
                                        title="Request Help"
                                    >
                                        <Headset className="h-4 w-4 lg:h-5 lg:w-5" />
                                    </button>
                                </motion.div>

                                {/* Technical Issue Button */}
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    transition={{ delay: 0.15 }}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="flex items-center gap-3 justify-end"
                                >
                                    <button
                                        onClick={() => handleOptionClick(1)}
                                        className="bg-white text-sahi-red px-3 py-2 rounded-lg shadow-md whitespace-nowrap text-sm font-medium transition-all duration-300 hover:shadow-lg cursor-pointer"
                                    >
                                        Report Bug
                                    </button>
                                    <button
                                        onClick={() => handleOptionClick(1)}
                                        className="w-10 h-10 lg:w-12 lg:h-12 rounded-full shadow-lg bg-sahi-red text-white flex items-center justify-center transition-all duration-300 hover:shadow-xl flex-shrink-0"
                                        title="Report Technical Issue"
                                    >
                                        <AlertTriangle className="h-4 w-4 lg:h-5 lg:w-5" />
                                    </button>
                                </motion.div>

                                {/* Feature Suggestion Button */}
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    transition={{ delay: 0.2 }}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="flex items-center gap-3 justify-end"
                                >
                                    <button
                                        onClick={() => handleOptionClick(2)}
                                        className="bg-white text-sahi-green px-3 py-2 rounded-lg shadow-md whitespace-nowrap text-sm font-medium transition-all duration-300 hover:shadow-lg cursor-pointer"
                                    >
                                        Share Idea
                                    </button>
                                    <button
                                        onClick={() => handleOptionClick(2)}
                                        className="w-10 h-10 lg:w-12 lg:h-12 rounded-full shadow-lg bg-sahi-green text-white flex items-center justify-center transition-all duration-300 hover:shadow-xl flex-shrink-0"
                                        title="Suggest Feature"
                                    >
                                        <Lightbulb className="h-4 w-4 lg:h-5 lg:w-5" />
                                    </button>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>

                {/* Tooltip on hover for desktop */}
                <div className="absolute bottom-full right-0 mb-2 hidden lg:group-hover:block pointer-events-none">
                    <div className="bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                        Submit Feedback
                        <div className="absolute top-full right-2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-gray-800"></div>
                    </div>
                </div>
            </motion.div>

            {/* Training Dialog */}
            <QuickTraining
                isOpen={isQuickTrainingOpen}
                onClose={() => setIsQuickTrainingOpen(false)}
                trainingData={getTrainingData("TRAINING1")}
            />

            {/* Feedback Dialog */}
            <FeedbackDialog
                isOpen={isFeedbackOpen}
                onClose={handleFeedbackClose}
                selectedFeedbackType={selectedFeedbackType}
            />
        </>
    );
};

export default FloatingFeedbackButton;